import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {  useRef } from "react";
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Nationality = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddNationalityModal, setShowAddNationalityModal] = useState(false);
  const [showEditNationalityModal, setShowEditNationalityModal] = useState(false);
  const [nationalityName, setNationalityName] = useState('');
  const [countryId, setCountryId] = useState('');
  const [selectedNationality, setSelectedNationality] = useState({ NationalityId: null, NationalityName: '', CountryId: null });
  const [nationalities, setNationalities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all countries
  const fetchCountries = async () => {
  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await axios.get(
      `${API_BASE_URL}/api/master/countries/`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    setCountries(response.data);
  } catch (err) {
    setSnackbarSeverity('error');
    setSnackbarMessage('Failed to fetch countries.');
    setOpenSnackbar(true);
    if (err.response?.status === 401) {
      localStorage.removeItem('accessToken');
      navigate('/login');
    }
  }
};
const [showAddModal, setShowAddModal] = useState(false);
const inputRef = useRef(null);
useEffect(() => {
  if (showAddModal) {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }
}, [showAddModal]);


  // Fetch all nationalities
  const fetchNationalities = async () => {
  setLoading(true); // Set loading to true before API call
  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await axios.get(
      `${API_BASE_URL}/api/master/nationalities/`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    const mappedNationalities = response.data.map(nationality => ({
      NationalityId: nationality.id,
      NationalityName: nationality.name,
      CountryId: nationality.country,
      CountryName:
        countries.find(c => c.id === nationality.country)?.name || 'Unknown',
    }));

    setNationalities(mappedNationalities);
    setFilteredData(mappedNationalities);
  } catch (err) {
    setSnackbarSeverity('error');
    setSnackbarMessage('Failed to fetch nationalities.');
    setOpenSnackbar(true);
  } finally {
    setLoading(false); // Set loading to false after API call
  }
};



  // Add a new nationality
  const handleSave = async (e) => {
    e.preventDefault();
    if (!nationalityName || !countryId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Nationality Name and Country are required.');
      setOpenSnackbar(true);
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/master/nationalities/`,
        { name: nationalityName, country: countryId },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const newNationality = {
        NationalityId: response.data.id,
        NationalityName: response.data.name,
        CountryId: response.data.country,
        CountryName: countries.find(country => country.id === response.data.country)?.name || 'Unknown',
      };
      setNationalities([...nationalities, newNationality]);
      setFilteredData([...filteredData, newNationality]);
      setShowAddNationalityModal(false);
      setSnackbarMessage('Nationality added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setNationalityName('');
      setCountryId('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to add nationality.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Delete a nationality
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/nationalities/${selectedNationality.NationalityId}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const updatedNationalities = nationalities.filter((nationality) => nationality.NationalityId !== selectedNationality.NationalityId);
      setNationalities(updatedNationalities);
      setFilteredData(updatedNationalities);
      setShowDeleteModal(false);
      setSnackbarMessage('Nationality deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete nationality.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Edit a nationality
  const handleUpdate = async () => {
    if (!nationalityName || !countryId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Nationality Name and Country are required.');
      setOpenSnackbar(true);
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/master/nationalities/${selectedNationality.NationalityId}/`,
        { name: nationalityName, country: countryId },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const updatedNationality = {
        NationalityId: response.data.id,
        NationalityName: response.data.name,
        CountryId: response.data.country,
        CountryName: countries.find(country => country.id === response.data.country)?.name || 'Unknown',
      };
      const updatedNationalities = nationalities.map((nationality) =>
        nationality.NationalityId === selectedNationality.NationalityId ? updatedNationality : nationality
      );
      setNationalities(updatedNationalities);
      setFilteredData(updatedNationalities);
      setShowEditNationalityModal(false);
      setSnackbarMessage('Nationality updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setNationalityName('');
      setCountryId('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to update nationality.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  const handleAddNationality = () => {
    setNationalityName('');
    setCountryId('');
    setShowAddNationalityModal(true);
  };

  const handleEdit = (nationality) => {
    setSelectedNationality({
      NationalityId: nationality.NationalityId,
      NationalityName: nationality.NationalityName,
      CountryId: nationality.CountryId
    });
    setNationalityName(nationality.NationalityName);
    setCountryId(nationality.CountryId);
    setShowEditNationalityModal(true);
  };

  const handleShowDeleteModal = (NationalityId, NationalityName) => {
    setSelectedNationality({ NationalityId, NationalityName });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleReset = () => {
    setNationalityName('');
    setCountryId('');
  };

  const handleCancel = () => {
    setShowEditNationalityModal(false);
    setNationalityName('');
    setCountryId('');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

useEffect(() => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    navigate('/login');
  } else {
    fetchCountries();
  }
}, [navigate]);

useEffect(() => {
  if (countries.length > 0) {
    fetchNationalities();
  }
}, [countries]);



  useEffect(() => {
    setFilteredData(
      nationalities.filter((nationality) =>
        nationality.NationalityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nationality.CountryName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, nationalities]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddNationality}
            sx={{
              ml: 2,
              width: '170px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD NATIONALITY
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Nationality</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Country</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
             <TableBody>
  {loading ? (
    <TableRow sx={{ height: 240 }}>
      <TableCell colSpan={4} align="center">
        Loading...
      </TableCell>
    </TableRow>
  ) : paginatedData.length > 0 ? (
    paginatedData.map((nationality, index) => (
      <TableRow
        key={nationality.NationalityId}
        sx={{
          backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
        <TableCell sx={{ fontSize: '1rem' }}>{nationality.NationalityName}</TableCell>
        <TableCell sx={{ fontSize: '1rem' }}>{nationality.CountryName}</TableCell>
        <TableCell align="center">
          <IconButton
            onClick={() => handleEdit(nationality)}
            sx={{
              color: theme.palette.primary.light,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleShowDeleteModal(nationality.NationalityId, nationality.NationalityName)}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    ))
  ) : (
    <TableRow sx={{
      backgroundColor: theme.palette.background.paper,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    }}>
      
    </TableRow>
  )}
</TableBody>

            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {/* {filteredData.length > 0 && ( */}
      {!loading && filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedNationality.NationalityName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Nationality Modal */}
      <Modal
        open={showAddNationalityModal}
        onClose={() => setShowAddNationalityModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add Nationality
            </Typography>
            <IconButton onClick={() => setShowAddNationalityModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="country-label" sx={{ color: theme.palette.text.primary }}>
                Country
              </InputLabel>
              <Select
                inputRef={inputRef}
                labelId="country-label"
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Nationality"
              variant="outlined"
              fullWidth
              value={nationalityName}
              onChange={(e) => setNationalityName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!countryId ? "Select Country" : !nationalityName ? "Enter Nationality Name" : ""} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!countryId || !nationalityName}
                  >
                    SAVE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleReset}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Edit Nationality Modal */}
      <Modal
        open={showEditNationalityModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit Nationality
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="country-label" sx={{ color: theme.palette.text.primary }}>
                Country
              </InputLabel>
              <Select
                labelId="country-label"
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Nationality"
              variant="outlined"
              fullWidth
              value={nationalityName}
              onChange={(e) => setNationalityName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!countryId ? "Select Country" : !nationalityName ? "Enter Nationality Name" : ""} arrow>
                <span>
                  <Button
                    variant="contained"
                    onClick={handleUpdate}
                    sx={{
                      mr: 2,
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': { backgroundColor: theme.palette.primary.dark },
                    }}
                    disabled={!countryId || !nationalityName}
                  >
                    UPDATE
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* User Guide Modal */}
     <Modal
  open={showUserGuideModal}
  onClose={() => setShowUserGuideModal(false)}
  BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 800,
      bgcolor: 'background.paper',
      borderRadius: '16px',
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
        Nationality Management - User Guide
      </Typography>
      <IconButton
        onClick={() => setShowUserGuideModal(false)}
        sx={{ color: '#fff', position: 'absolute', right: 14 }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
      {/* Section 1: Add Nationality */}
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
      >
        ➤ Follow these steps to add a new nationality:
      </Typography>
      <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
        <li>
          Click the <strong>"ADD NATIONALITY"</strong> button at the top right to open the Add Nationality dialog.
        </li>
        <li>
          Select the <strong>Country</strong> from the dropdown menu.
        </li>
        <li>
          Enter the <strong>Nationality Name</strong> in the provided text field.
        </li>
        <li>
          Before clicking <strong>"SAVE"</strong>, review the entered information for accuracy.
        </li>
        <li>
          Click <strong>"SAVE"</strong> to add the nationality. A success notification will confirm the addition.
        </li>
        <li>
          The new nationality will appear in the Nationality List table.
        </li>
        <li>
          To clear the form without saving, click <strong>"RESET"</strong>.
        </li>
      </ul>
      <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
        ⚠️ Note: Country and Nationality Name are required fields.
      </Typography>

      {/* Section 2: Edit Nationality */}
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
      >
        ➤ Follow these steps to update an existing nationality:
      </Typography>
      <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
        <li>
          Locate the nationality you want to update in the Nationality List table.
        </li>
        <li>
          Click the <strong>Edit</strong> icon (pencil) in the Action column to open the Edit Nationality dialog.
        </li>
        <li>
          Update the <strong>Country</strong> or <strong>Nationality Name</strong> as needed.
        </li>
        <li>
          Before clicking <strong>"UPDATE"</strong>, ensure all required fields are filled.
        </li>
        <li>
          Click <strong>"UPDATE"</strong> to save your changes. A success notification will confirm the update.
        </li>
        <li>
          To exit without saving, click <strong>"CANCEL"</strong>.
        </li>
      </ul>
      <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
        ⚠️ Note: Country and Nationality Name are required fields.
      </Typography>

      {/* Section 3: Delete Nationality */}
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
      >
        ➤ Follow these steps to delete a nationality:
      </Typography>
      <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
        <li>
          Find the nationality you want to delete in the Nationality List table.
        </li>
        <li>
          Click the <strong>Delete</strong> icon (trash) in the Action column.
        </li>
        <li>
          Confirm the deletion by clicking <strong>"Yes, Delete"</strong> in the confirmation dialog.
        </li>
        <li>
          The nationality will be removed from the list, and a success notification will appear.
        </li>
      </ul>
      <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
        ⚠️ Note: Deleted nationalities cannot be recovered. Proceed with caution.
      </Typography>

      {/* Section 4: Search Nationalities */}
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
      >
        ➤ Follow these steps to search for a nationality:
      </Typography>
      <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
        <li>
          Use the search bar at the top left of the Nationality List.
        </li>
        <li>
          Enter the Nationality Name or Country Name (or part of it) to filter the table.
        </li>
        <li>
          The table will update automatically to show matching nationalities.
        </li>
      </ul>
      <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
        ⚠️ Note: The search is case-insensitive and filters by Nationality Name and Country Name.
      </Typography>

      {/* Section 5: Pagination */}
      <Typography
        variant="body1"
        sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
      >
        ➤ Follow these steps to navigate the nationality list:
      </Typography>
      <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
        <li>
          Use the pagination controls at the bottom to navigate through pages of nationalities.
        </li>
        <li>
          Click the left (<strong>&lt;&lt;</strong>) or right (<strong>&gt;&gt;</strong>) arrows to move between pages.
        </li>
        <li>
          Select a specific page number to jump directly to that page.
        </li>
        <li>
          Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).
        </li>
      </ul>
      <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
        ⚠️ Note: The table displays only the selected number of rows per page.
      </Typography>
    </CustomTableScrollbar>
  </Box>
</Modal>

    </Container>
  );
};

export default Nationality;
